import React, { useState, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Modal,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { WebView, WebViewMessageEvent } from 'react-native-webview';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { initiateClubPayment, verifyClubPayment } from '../../lib/api/payments';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { Alert } from '../../utils/toast';

type ClubPaymentNavigationProp = StackNavigationProp<ClubsStackParamList, 'ClubPayment'>;
type ClubPaymentRouteProp = RouteProp<ClubsStackParamList, 'ClubPayment'>;

interface RazorpayResult {
  success: boolean;
  dismissed?: boolean;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  razorpay_signature?: string;
  error?: string;
}

const ClubPaymentScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<ClubPaymentNavigationProp>();
  const route = useRoute<ClubPaymentRouteProp>();
  const { clubId, clubName, amount } = route.params;

  const [isInitiating, setIsInitiating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [checkoutHtml, setCheckoutHtml] = useState<string | null>(null);
  const [webViewKey, setWebViewKey] = useState(0);
  const webViewRef = useRef<WebView>(null);

  const buildCheckoutHtml = (
    keyId: string,
    orderId: string,
    amountPaise: number,
    name: string,
    description: string,
    prefillName?: string,
    prefillContact?: string,
    prefillEmail?: string,
  ) => `<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <title>LNB Payment</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { width: 100%; height: 100%; background: #0d0d0d; }
    .msg { color: #fff; font-family: sans-serif; font-size: 16px;
           text-align: center; position: absolute;
           top: 50%; left: 50%; transform: translate(-50%, -50%); }
    .err { color: #ff6b6b; }
  </style>
</head>
<body>
  <div class="msg" id="status">Opening payment…</div>
  <script>
    function onScriptError() {
      document.getElementById('status').className = 'msg err';
      document.getElementById('status').innerText = 'Failed to load payment gateway. Check connection.';
      window.ReactNativeWebView.postMessage(JSON.stringify({ success: false, error: 'checkout_js_load_failed' }));
    }
  </script>
  <script src="https://checkout.razorpay.com/v1/checkout.js" onerror="onScriptError()"></script>
  <script>
    (function() {
      if (!window.Razorpay) {
        onScriptError();
        return;
      }
      var options = {
        key: ${JSON.stringify(keyId)},
        amount: ${amountPaise},
        currency: "INR",
        order_id: ${JSON.stringify(orderId)},
        name: "LNB — Club Membership",
        description: ${JSON.stringify(description)},
        prefill: {
          name: ${JSON.stringify(prefillName || '')},
          contact: ${JSON.stringify(prefillContact || '')},
          email: ${JSON.stringify(prefillEmail || '')}
        },
        theme: { color: "#C4963E" },
        handler: function(response) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            success: true,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          }));
        },
        modal: {
          ondismiss: function() {
            window.ReactNativeWebView.postMessage(JSON.stringify({ success: false, dismissed: true }));
          },
          escape: false,
          handleback: true
        }
      };
      try {
        var rzp = new Razorpay(options);
        rzp.on('payment.failed', function(resp) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            success: false,
            error: resp.error && resp.error.description ? resp.error.description : 'Payment failed'
          }));
        });
        document.getElementById('status').innerText = '';
        rzp.open();
      } catch(e) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ success: false, error: e.message || 'Init failed' }));
      }
    })();
  </script>
</body>
</html>`;

  // Web-specific handler: load Razorpay checkout.js directly in the browser
  const handleOpenRazorpayWeb = async () => {
    try {
      setIsInitiating(true);
      const order = await initiateClubPayment(clubId);
      setIsInitiating(false);

      // Inject checkout.js once
      await new Promise<void>((resolve, reject) => {
        if ((window as any).Razorpay) { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
        document.head.appendChild(script);
      });

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: 'INR',
        order_id: order.orderId,
        name: 'LNB — Club Membership',
        description: order.description,
        prefill: {
          name: order.prefillName ?? '',
          contact: order.prefillContact ?? '',
          email: order.prefillEmail ?? '',
        },
        theme: { color: '#C4963E' },
        handler: async (response: any) => {
          try {
            setIsVerifying(true);
            await verifyClubPayment({
              clubId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            showSuccessToast('Payment successful! 🎉 Enter the club code to complete joining.');
            navigation.replace('EnterClubCode', { clubId, clubName });
          } catch (err: any) {
            showErrorToast(err.message || 'Payment verification failed. Contact support.');
          } finally {
            setIsVerifying(false);
          }
        },
        modal: { ondismiss: () => {} },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', (resp: any) => {
        showErrorToast(resp.error?.description || 'Payment failed. Please try again.');
      });
      rzp.open();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to initiate payment');
      setIsInitiating(false);
      setIsVerifying(false);
    }
  };

  const handleOpenRazorpay = async () => {
    if (Platform.OS === 'web') {
      await handleOpenRazorpayWeb();
      return;
    }
    // Android / iOS — use embedded WebView
    try {
      setIsInitiating(true);
      const order = await initiateClubPayment(clubId);
      const html = buildCheckoutHtml(
        order.keyId,
        order.orderId,
        order.amount,           // backend returns paise
        order.clubName,
        order.description,
        order.prefillName,
        order.prefillContact,
        order.prefillEmail,
      );
      setCheckoutHtml(html);
      setWebViewKey((k) => k + 1);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to initiate payment');
    } finally {
      setIsInitiating(false);
    }
  };

  const handleWebViewMessage = async (event: WebViewMessageEvent) => {
    let result: RazorpayResult;
    try {
      result = JSON.parse(event.nativeEvent.data);
    } catch {
      return;
    }

    if (result.dismissed) {
      // User closed modal — go back to checkout page
      setCheckoutHtml(null);
      return;
    }

    if (!result.success) {
      setCheckoutHtml(null);
      showErrorToast(result.error || 'Payment failed. Please try again.');
      return;
    }

    // Verify with backend
    try {
      setIsVerifying(true);
      setCheckoutHtml(null);
      await verifyClubPayment({
        clubId,
        razorpayOrderId: result.razorpay_order_id!,
        razorpayPaymentId: result.razorpay_payment_id!,
        razorpaySignature: result.razorpay_signature!,
      });

      showSuccessToast('Payment successful! 🎉 You are now a member.');
      navigation.replace('ClubDetail', { clubId });
    } catch (error: any) {
      showErrorToast(error.message || 'Payment verification failed. Contact support.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (checkoutHtml) {
    return (
      <Modal
        visible
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setCheckoutHtml(null)}
      >
        <SafeAreaView style={styles.fullScreenWebView} edges={['top', 'bottom']}>
          <WebView
            key={webViewKey}
            ref={webViewRef}
            // baseUrl gives the injected HTML a real origin so Razorpay's
            // checkout.js can make same-origin API calls without CORS errors.
            source={{ html: checkoutHtml, baseUrl: 'https://checkout.razorpay.com' }}
            onMessage={handleWebViewMessage}
            javaScriptEnabled
            domStorageEnabled
            thirdPartyCookiesEnabled
            originWhitelist={['*']}
            mixedContentMode="compatibility"
            allowsInlineMediaPlayback
            style={styles.webViewFill}
            setSupportMultipleWindows={false}
            startInLoadingState
            // Forward UPI / wallet deep-links to the OS so payment apps open correctly.
            // Allow only http/https/about/blob/data/javascript — anything else is a
            // native app deep-link (upi://, credpay://, phonepe://, gpay://, etc.)
            // and must be handed off to the OS via Linking.
            onShouldStartLoadWithRequest={(request) => {
              const { url } = request;
              const allowed = ['http://', 'https://', 'about:', 'blob:', 'data:', 'javascript:'];
              if (allowed.some((prefix) => url.startsWith(prefix))) return true;
              // Deep-link — open in the OS and block WebView from navigating
              Linking.openURL(url).catch(() => {});
              return false;
            }}
            onError={() => {
              // Navigation error (e.g. ERR_UNKNOWN_URL_SCHEME from a failed deep-link) —
              // dismiss the WebView so the user sees the payment summary instead of a blank page.
              setCheckoutHtml(null);
            }}
            renderLoading={() => (
              <View style={[styles.webViewLoading, { backgroundColor: '#0d0d0d' }]}>
                <ActivityIndicator color="#C4963E" size="large" />
              </View>
            )}
          />
        </SafeAreaView>
      </Modal>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right', 'bottom']}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Club Membership</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.content}>
        {/* Club Summary Card */}
        <View style={[styles.summaryCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={[styles.summaryIconCircle, { backgroundColor: theme.primary + '20' }]}>
            <Ionicons name="people" size={36} color={theme.primary} />
          </View>
          <ThemedText style={[styles.clubName, { color: theme.text }]}>{clubName}</ThemedText>
          <ThemedText style={[styles.membershipLabel, { color: theme.textSecondary }]}>Annual Membership</ThemedText>

          <View style={[styles.amountRow, { borderTopColor: theme.border }]}>
            <ThemedText style={[styles.amountLabel, { color: theme.textSecondary }]}>Amount</ThemedText>
            <ThemedText style={[styles.amountValue, { color: theme.text }]}>
              ₹{amount.toLocaleString('en-IN')}
            </ThemedText>
          </View>
          <View style={styles.amountRow}>
            <ThemedText style={[styles.amountLabel, { color: theme.textSecondary }]}>Validity</ThemedText>
            <ThemedText style={[styles.amountValue, { color: theme.text }]}>1 Year</ThemedText>
          </View>
          <View style={styles.amountRow}>
            <ThemedText style={[styles.amountLabel, { color: theme.textSecondary }]}>Currency</ThemedText>
            <ThemedText style={[styles.amountValue, { color: theme.text }]}>INR ₹</ThemedText>
          </View>
        </View>

        {/* Security note */}
        <View style={[styles.securityNote, { backgroundColor: theme.primary + '15', borderColor: theme.primary + '40' }]}>
          <Ionicons name="shield-checkmark" size={18} color={theme.primary} />
          <ThemedText style={[styles.securityText, { color: theme.textSecondary }]}>
            Payments are processed securely via Razorpay. Your card details are never stored by LNB.
          </ThemedText>
        </View>

        {/* Post-payment note */}
        <View style={[styles.infoNote, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Ionicons name="checkmark-circle" size={18} color={theme.accent} />
          <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
            After successful payment you will be automatically added to the club. No extra steps needed.
          </ThemedText>
        </View>
      </View>

      {/* Pay Button */}
      <View style={[styles.bottomBar, { backgroundColor: theme.backgroundSecondary, borderTopColor: theme.border }]}>
        <TouchableOpacity
          style={[styles.payButton, { backgroundColor: isInitiating ? theme.disabled : theme.primary }]}
          onPress={handleOpenRazorpay}
          activeOpacity={0.7}
          disabled={isInitiating || isVerifying}
        >
          {isInitiating || isVerifying ? (
            <ActivityIndicator color="#000000" />
          ) : (
            <>
              <Ionicons name="card" size={20} color="#000000" />
              <ThemedText style={styles.payButtonText}>
                Pay ₹{amount.toLocaleString('en-IN')}
              </ThemedText>
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  summaryCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    alignItems: 'center',
    marginBottom: 16,
  },
  summaryIconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  clubName: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 4,
  },
  membershipLabel: {
    fontSize: 14,
    marginBottom: 16,
  },
  amountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: 'transparent',
  },
  amountLabel: {
    fontSize: 14,
  },
  amountValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  securityNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    marginBottom: 12,
  },
  securityText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
  },
  infoNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
  },
  bottomBar: {
    padding: 16,
    borderTopWidth: 1,
  },
  payButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 12,
  },
  payButtonText: {
    fontSize: 17,
    fontWeight: '700',
    color: '#000000',
  },
  fullScreenWebView: {
    flex: 1,
    backgroundColor: '#0d0d0d',
  },
  webViewFill: {
    flex: 1,
    backgroundColor: '#0d0d0d',
  },
  webViewLoading: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ClubPaymentScreen;
